import { twMerge } from "tailwind-merge";

import { containsClosingPhrase } from "@/hooks/validation";
import {
  AssessmentQuestionFields,
  type GenerateInputsMap,
  type FieldValidationState,
  type GenerateResponse,
} from "@/types/screener-builder";
import { AlertSeverity, type Nullable } from "@/types/utils";
import { anyEmptyStrings } from "@/utils/helpers";

const getCharCountValidation = (maxChars: number) => (value: string) => {
  const valueLength = value.length;

  if (valueLength > maxChars) {
    return {
      error: `Max ${maxChars} characters (${valueLength}/${maxChars})`,
      valid: false,
      validationErrors: [
        `Input is over the character limit (${valueLength}/${maxChars} characters)`,
      ],
    };
  }

  const isEmpty = anyEmptyStrings(value);
  return {
    error: null,
    valid: !isEmpty,
    validationErrors: isEmpty ? ["Input is empty"] : [],
  };
};

export const FIELD_CONFIG: {
  [key in AssessmentQuestionFields]: {
    analyticsName: string;
    disabledGenerateTooltip: string;
    // Returns the available inputs for the field. Returns null if required
    // fields are not present. This function is not error-aware
    getGenerateInputs: (
      competencyName: string,
      competencyDefinition: string,
      question: string,
    ) => Nullable<GenerateInputsMap[key]>;
    helpLink: string;
    labelText: string;
    regenerationWarningName: string;
    validate: (value: string) => FieldValidationState;
  };
} = {
  [AssessmentQuestionFields.Competency]: {
    analyticsName: "Competency Name",
    disabledGenerateTooltip: "Requires a competency definition or question",
    getGenerateInputs: (competencyName, competencyDefinition, question) =>
      !anyEmptyStrings(competencyDefinition) || !anyEmptyStrings(question)
        ? { competencyDefinition, question }
        : null,
    helpLink:
      "https://intercom.help/upwage-support/en/articles/11482999-tips-for-picking-the-right-competencies-for-a-role",
    labelText: "Competency",
    regenerationWarningName: "competency",
    validate: getCharCountValidation(50),
  },
  [AssessmentQuestionFields.Definition]: {
    analyticsName: "Competency Definition",
    disabledGenerateTooltip: "Requires a competency name",
    getGenerateInputs: (competencyName, competencyDefinition, question) =>
      !anyEmptyStrings(competencyName) ? { competencyName, question } : null,
    helpLink:
      "https://intercom.help/upwage-support/en/articles/11385626-tips-for-writing-great-competency-definitions",
    labelText: "Competency definition",
    regenerationWarningName: "definition",
    validate: getCharCountValidation(600),
  },
  [AssessmentQuestionFields.Question]: {
    analyticsName: "Question",
    disabledGenerateTooltip: "Requires a competency name or definition",
    getGenerateInputs: (competencyName, competencyDefinition) =>
      !anyEmptyStrings(competencyName) || !anyEmptyStrings(competencyDefinition)
        ? { competencyDefinition, competencyName }
        : null,
    helpLink:
      "https://intercom.help/upwage-support/en/articles/11386202-tips-for-writing-better-assessment-questions",
    labelText: "Question",
    regenerationWarningName: "question(s)",
    validate: (question) => {
      const {
        error: charCountError,
        validationErrors: charCountValidationErrors,
      } = getCharCountValidation(250)(question);
      const { errorMessage: closingPhraseErrorMessage } = containsClosingPhrase(
        question,
        {
          formatFn: (phrase) =>
            `Questions can't contain the phrase "${phrase}"`,
        },
      );
      const threeOrMoreQuestionMarks = /\?[^?]+\?[^?]+\?/.test(question);
      const looksLikeYesNo =
        question.split(" ").filter(Boolean).length > 1 &&
        /^(Is|Are|Do|Does|Did|Have|Had)\b/i.test(question);
      const errors = [
        charCountError,
        closingPhraseErrorMessage,
        threeOrMoreQuestionMarks
          ? "Avoid asking multiple questions at the same time"
          : null,
      ].filter((e) => !!e);

      return {
        error: errors.length > 0 ? errors.join("\n") : null,
        valid: !!question && errors.length === 0,
        validationErrors: [
          ...charCountValidationErrors,
          closingPhraseErrorMessage &&
            "Question contains inappropriate closing phrase",
          threeOrMoreQuestionMarks &&
            "Question contains multiple question marks",
          looksLikeYesNo && "Question appears to be a yes/no question",
        ].filter(Boolean) as string[],
        warning: looksLikeYesNo
          ? {
              message:
                "This looks like a yes/no question. To get the most helpful responses, we recommend asking more open-ended questions.",
              severity: AlertSeverity.Info,
            }
          : undefined,
      };
    },
  },
};

export const OTHER_FIELD_LABELS = {
  customDisplayName: "Use custom display name",
  customQuestion: "Use custom question",
  customRubric: "Use custom scoring rubric",
  highScoringAnswer: "High-scoring answer description",
  lowScoringAnswer: "Low-scoring answer description",
  mediumScoringAnswer: "Medium-scoring answer description",
};

export const baseLabelClassname = "font-semibold text-sm text-uw-gray-9";

export const getSharedInputClassNames = (isTextarea = true) => ({
  error: "whitespace-pre-wrap",
  input: twMerge([
    "border-uw-gray-2",
    "disabled:text-uw-gray-7",
    "focus:border-uw-purple",
    "data-[error=true]:border-uw-red",
    "px-3.5",
    "py-3",
    isTextarea ? "h-[84px]" : "h-11",
  ]),
  label: "w-full",
  required: "text-uw-gray-9",
});

export const validateRubricField = (value: string, maxChars = 800) => {
  return getCharCountValidation(maxChars)(value);
};

// Simplified content extraction utilities for retry context
export const simplifyContent = {
  competency: (content: string) => content,
  definition: (content: string) => content,
  question: (
    content: GenerateResponse<AssessmentQuestionFields.Question>["content"],
  ) => content.map((q) => q.question),
};

export const CANCEL_CONFIRMATION_PROPS = {
  cancelButtonContent: "Keep editing",
  message: "Are you sure you want to cancel? Your changes will be discarded.",
  primaryButtonContent: "Discard changes",
} as const;
